import type { ComplianceState } from '../../types/compliance';
import type { ProductBalance } from '../../types/finance';
import { isMfaCode } from '../../lib/mfaApproval';

/**
 * Decisions behind the estate's exit at a term end on the client compliance panel (owner,
 * 2026-11-03, S-61 amended). Pure, so each rule is tested without a DOM. The API decides; these
 * only keep the panel from offering what it would refuse.
 */

/** The succession case's states from which the exit may be given: the death is verified and no
 *  legal hold stands (the server's `TERM_END_EXIT_STATES`). */
export const ESTATE_EXIT_STATES: readonly string[] = [
  'death_verified',
  'in_succession',
  'estate_represented',
];

/** The reason the API asks for, in characters. */
export const ESTATE_EXIT_REASON_MIN = 10;

/** Whether the panel offers the estate's exit: a death notice in force and a case past the
 *  confirmation of the death, not on legal hold. */
export function canGiveEstateExit(state: ComplianceState | null): boolean {
  return (
    state !== null &&
    state.death_notice_active &&
    Boolean(state.succession_case_id) &&
    ESTATE_EXIT_STATES.includes(state.succession_case_state ?? '')
  );
}

/** The holdings the exit applies to: a product held to the end of its term, with something in it. */
export function exitableHoldings(items: readonly ProductBalance[]): ProductBalance[] {
  return items.filter((item) => item.no_early_exit === true && Number(item.balance) > 0);
}

/** Why the exit's button is still off; null when it can go. */
export function estateExitBlockedReason(
  productId: string, reason: string, code: string,
): string | null {
  if (productId === '') return 'Choose the holding to exit.';
  if (reason.trim().length < ESTATE_EXIT_REASON_MIN) {
    return 'Write a reason of at least ten characters.';
  }
  return isMfaCode(code) ? null : 'Enter the code from your authenticator app.';
}
